import { useCallback, useContext, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@Hooks/index';
import { useNavigate } from 'react-router-dom';
import { AnyObject } from 'antd/es/_util/type';

import { purchaseRequestActions } from '@Features/MaterialRequest/requests/PurchaseRequest/store/purchaseRequestSlice';
import { createdTender } from '@Features/MaterialRequest/requests/PurchaseRequest/store/selectors/purchaseRequestSelector';
import { NotificationContext } from '@Context/NotificationContext';
import MRFeatureRoutePath from '@Features/router/mr/MRFeatureRoutePath.enum';
import { TenderMaterialListResponse } from '@Types/api/form/tenderRequest/TenderMaterialResponse';
import { RequiredNotification, ViewResolver } from '@Components/index';
import { MButton, MTable } from '@Components/Base';
import TenderConfirmMobile from './mobile/TenderConfirmMobile';
import { getColumns } from './DataSource/MapColumns';

import { TenderConfirmationActions } from './components';
import { ConfirmationCard } from './mobile/ConfirmationCard';
import TenderConfirmDesktop from './desktop/TenderConfirmDesktop';
import {
  useDeleteTenderMaterialsMutation,
  useGetTenderMaterialsQuery,
} from '../../store/api/TenderRequestApiSlice';

import styles from './tender-confirm-screen.module.css';

const backText = 'Back to procurement';

const captionText = 'Tender Confirmation';

const TenderConfirmScreen: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [showNotification, setShowNotification] = useState(false);
  const openNotification = (): void => setShowNotification(true);
  const closeNotification = (): void => setShowNotification(false);
  const [materials, setMaterials] = useState<TenderMaterialListResponse>([]);
  const [materialsToDelete, setMaterialsToDelete] = useState<string[]>([]);
  const tenderId = useAppSelector(createdTender);
  const { successNotification, errorNotification } =
    useContext(NotificationContext);
  const [deleteTenderMaterials] = useDeleteTenderMaterialsMutation();

  const { data } = useGetTenderMaterialsQuery(String(tenderId), {
    skip: !tenderId,
  });

  const handleBack = (): void => {
    closeNotification();
    dispatch(purchaseRequestActions.setCreatedTenderId(0));
    navigate(
      `/mr/${MRFeatureRoutePath.PROCUREMENT}/${MRFeatureRoutePath.PROCUREMENT_PURCHASE_REQUEST}`
    );
  };

  const handleDelete = (record: AnyObject): void => {
    setMaterialsToDelete((prevMaterialsToDelete) => [
      ...prevMaterialsToDelete,
      record.tenderItemID,
    ]);
    setMaterials(
      materials.filter(
        (material) => material.materialCode !== record.materialCode
      )
    );
  };

  const handleConfirmRequest = useCallback((): void => {
    deleteTenderMaterials({ tenderId, tenderItemIds: materialsToDelete })
      .unwrap()
      .then(() => {
        successNotification(`Tender No. ${tenderId} has been updated!`);
        dispatch(purchaseRequestActions.setCreatedTenderId(0));
        navigate(
          `/mr/${MRFeatureRoutePath.PROCUREMENT}/${MRFeatureRoutePath.PROCUREMENT_PURCHASE_REQUEST}`
        );
      })
      .catch((error) => {
        errorNotification(error.data);
      });
  }, [
    deleteTenderMaterials,
    dispatch,
    errorNotification,
    materialsToDelete,
    navigate,
    successNotification,
    tenderId,
  ]);

  const mapColumns = getColumns(handleDelete);

  useEffect(() => {
    if (data) {
      const updatedData = data.map((item, index) => ({
        ...item,
        indexNo: String(index + 1).padStart(2, '0'),
      }));
      setMaterials(updatedData);
    }
  }, [data]);

  return (
    <>
      <ViewResolver
        desktop={
          <TenderConfirmDesktop
            handleBack={
              materialsToDelete.length > 0 ? openNotification : handleBack
            }
            backToText={backText}
            handleConfirm={handleConfirmRequest}
            caption={captionText}
            actions={<TenderConfirmationActions />}
            hidePagination={false}
            paginationSettings={{
              current: 1,
              total: materials.length,
              pageSize: 10,
              onChange: () => null,
            }}
            content={
              <MTable
                className={styles.table}
                columns={mapColumns}
                dataSource={materials}
                hidePagination
              />
            }
          />
        }
        mobile={
          <TenderConfirmMobile
            handleBack={
              materialsToDelete.length > 0 ? openNotification : handleBack
            }
            handleConfirm={handleConfirmRequest}
            disableConfirm={!materials.length}
            caption={captionText}
            content={
              <>
                {materials.map((item) => (
                  <ConfirmationCard
                    title={item.materialDesc}
                    action={
                      <MButton
                        className={styles.deleteButton}
                        type="text"
                        onClick={() => handleDelete(item)}
                      >
                        Delete
                      </MButton>
                    }
                    items={[
                      { label: 'Activity Code', value: item.materialCode },
                      { label: 'Qty.', value: item.reqQty.toString() },
                      { label: 'Material Code', value: item.materialCode },
                    ]}
                  />
                ))}
              </>
            }
          />
        }
      />
      <RequiredNotification
        title="Unsaved Changes"
        message="Unsaved changes will be lost. Are you sure you want to leave this page?"
        isModalOpen={showNotification}
        textSuccessBtn="Yes"
        textCancelBtn="No"
        handleCancel={closeNotification}
        handleSuccess={handleBack}
      />
    </>
  );
};

export default TenderConfirmScreen;
